import {
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import fetcher from "@/core/lib/fetcher";
import type { APIResponse } from "@/core/types/utils";
import type {
	CollectionAdmin,
	CollectionAdminListItem,
	CollectionCreatePayload,
	CollectionUpdatePayload,
} from "./collection.types";

const COLLECTIONS_URL = `${import.meta.env.VITE_API_BOOKING}property/collections`;

export const getCollectionsQueryOptions = (
	token: string,
	organizationsIds?: string,
) =>
	queryOptions({
		// organizationsIds is part of the request, so it must be part of the key —
		// otherwise switching the organization filter serves the previous list
		// from cache (the bug getGroupsQueryOptions used to have).
		queryKey: ["collections", organizationsIds],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${token}`)
				.get(`${COLLECTIONS_URL}?organizationIds=${organizationsIds ?? "All"}`)
				.json<Promise<APIResponse<CollectionAdminListItem[]>>>(),
	});

export const getCollectionByIdQueryOptions = (id: string, token: string) =>
	queryOptions({
		queryKey: ["collection", id, token],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${token}`)
				.get(`${COLLECTIONS_URL}/${id}`)
				.json<Promise<APIResponse<CollectionAdmin>>>(),
	});

export const createCollection = (
	data: CollectionCreatePayload,
	token: string,
) =>
	fetcher
		.auth(`Bearer ${token}`)
		.url(COLLECTIONS_URL)
		.post(data)
		.json<Promise<APIResponse<CollectionAdmin>>>();

export const updateCollection = (
	data: CollectionUpdatePayload,
	id: string,
	token: string,
) =>
	fetcher
		.auth(`Bearer ${token}`)
		.url(`${COLLECTIONS_URL}/${id}`)
		.put(data)
		.json<Promise<{ data: { id: string } }>>();

/** Soft delete: archives the Collection without touching its member hotels. */
export const useDeleteCollection = (token: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			fetcher
				.auth(`Bearer ${token}`)
				.url(`${COLLECTIONS_URL}/${id}`)
				.delete()
				.json(),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: ["collections"],
			});
		},
	});
};
